// Editor note operations, ported faithfully from poc/web/app.js (addNote/addBeatNote/flowToNext
// /ensureRoom/advanceMeasure/addRest/applyDur/deleteNote/materialize). The PoC mutated globals; here
// they mutate an explicit EditCtx so the store layer can clone-in, run, and commit-out.
import type { ChartMeasure, NoteEvent, NoteDuration, TechniqueId } from "../../types/domain";
import { fxList, toggleFx } from "../../types/domain";
import { userBeats, rawToBeatIndex, beatIndexToRawIn, beatsOf } from "../notation/beatGrouping";

export interface EditCtx {
  notes: Record<number, NoteEvent[]>;     // per-measure editable notes (arrangement)
  ts: Record<number, [number, number]>;   // per-measure time signature override
  chartMeasures: ChartMeasure[];
  editMode: boolean;
  activeMeasure: number;
  selectedNoteIdx: number;
  insertArmed: boolean;
  curDur: NoteDuration;
  curDotted: boolean;
  curTriplet: boolean;
  curFx: TechniqueId[];
  curTie: boolean;
  curGrace: "" | "h" | "sl";
  curRestDotted: boolean;
}

const raw = (c: EditCtx): NoteEvent[] => c.notes[c.activeMeasure] || (c.notes[c.activeMeasure] = []);
// a fresh primary beat note carrying the pen's current length + dot + triplet. In 추가(insert) mode the
// pen also carries the armed technique/tie/grace so the NEW note gets them (set without touching the
// previously-added note); in 선택(select) mode those start clean.
const newNote = (c: EditCtx, string: number, fret: number): NoteEvent => {
  const n: NoteEvent = { string, fret, dur: c.curDur, fx: c.insertArmed && c.curFx.length ? c.curFx.slice() : null };
  if (c.curDotted) n.dotted = true;
  if (c.curTriplet) n.triplet = true;
  if (c.insertArmed && c.curTie) n.tie = true;
  if (c.insertArmed && c.curGrace) { n.grace = true; if (c.curGrace === "sl") n.graceSlide = true; }
  return n;
};
const tsOf = (c: EditCtx, m: number): [number, number] =>
  c.ts[m] || [(c.chartMeasures[m]?.beats ? c.chartMeasures[m].beats.length : 4), 4];

function restsForMeasure(c: EditCtx, m: number, dur: NoteDuration): NoteEvent[] {
  const ts = tsOf(c, m);
  const cb = c.chartMeasures[m]?.beats || [];
  return Array.from({ length: ts[0] }, (_, b) => {
    const o: NoteEvent = { rest: true, dur };
    if (cb[b] && cb[b].changed && cb[b].chord) o.chordLabel = cb[b].chord!;
    return o;
  });
}

// edit mode: pick a measure as active, materializing it into editable rests (carrying chord labels)
export function materializeMeasure(c: EditCtx, m: number, beatInBar: number): void {
  c.activeMeasure = m;
  if (!c.notes[m] || !c.notes[m].length) {
    c.notes[m] = restsForMeasure(c, m, (tsOf(c, m)[1] as NoteDuration) || 4);
  }
  c.selectedNoteIdx = beatIndexToRawIn(c.notes[m], beatInBar);
}

function ensureRoomForBeats(c: EditCtx, addBeats: number): boolean {
  if (!c.editMode || c.activeMeasure < 0) return true;
  const cap = tsOf(c, c.activeMeasure)[0] || 4;
  while (beatsOf(raw(c)) + addBeats > cap + 1e-6) {
    const r = raw(c);
    const last = r[r.length - 1];
    if (last && last.rest) r.pop();
    else return false;
  }
  return true;
}

function advanceMeasureForAdd(c: EditCtx): boolean {
  const next = c.activeMeasure + 1;
  if (next < 0 || next >= c.chartMeasures.length) return false;
  if (!c.notes[next] || !c.notes[next].length) c.notes[next] = restsForMeasure(c, next, 4);
  c.activeMeasure = next;
  return true;
}

function flowToNextMeasure(c: EditCtx, string: number, fret: number): boolean {
  if (!advanceMeasureForAdd(c)) return false;
  const r = raw(c);
  const ri = r.findIndex((x) => x.rest);
  if (ri >= 0) r[ri] = newNote(c, string, fret);
  else r.unshift(newNote(c, string, fret));
  c.selectedNoteIdx = ri >= 0 ? ri : 0;
  return true;
}

function addBeatNote(c: EditCtx, string: number, fret: number): boolean {
  if (!ensureRoomForBeats(c, 4 / c.curDur)) {
    return flowToNextMeasure(c, string, fret);
  }
  const r = raw(c);
  let at: number;
  if (c.selectedNoteIdx >= 0) at = c.selectedNoteIdx + 1;
  else { at = r.length; while (at > 0 && r[at - 1] && r[at - 1].rest) at--; }
  r.splice(at, 0, newNote(c, string, fret));
  c.selectedNoteIdx = at;
  return true;
}

// insert (추가) mode: ALWAYS insert a brand-new beat right AFTER the selected one (between it and the
// next beat) — never overwrite/consume an adjacent rest. If the bar is full of notes it flows to the
// next measure; trailing rests are popped to make room so the bar length stays valid.
function insertTap(c: EditCtx, string: number, fret: number): boolean {
  const r = raw(c);
  const cap = tsOf(c, c.activeMeasure)[0] || 4;
  const groups = userBeats(r);
  let bi = c.selectedNoteIdx >= 0 ? rawToBeatIndex(r, c.selectedNoteIdx) : groups.length;
  if (bi < 0) bi = groups.length;   // selection past the end → append path

  if (bi < groups.length) {
    // insert a new beat right after the selected beat (shifting everything after it along)
    if (!ensureRoomForBeats(c, 4 / c.curDur)) return flowToNextMeasure(c, string, fret);
    const ng = userBeats(raw(c));
    const after = ng[bi] ? ng[bi].items[ng[bi].items.length - 1].idx + 1 : raw(c).length;
    raw(c).splice(after, 0, newNote(c, string, fret));
    c.selectedNoteIdx = after;
    return true;
  }

  // ── append path (selection past the end) ──
  if (beatsOf(r) < cap - 1e-6) {
    r.push(newNote(c, string, fret));
    if (beatsOf(r) < cap - 1e-6) {
      c.selectedNoteIdx = r.length;                       // still room → keep appending
    } else if (advanceMeasureForAdd(c)) {                 // just filled the bar → jump to next measure now
      const nb = userBeats(raw(c)); c.selectedNoteIdx = nb.length ? nb[0].items[0].idx : -1;
    } else {
      c.selectedNoteIdx = r.length - 1;                   // no next measure → stay on the last note
    }
    return true;
  }
  if (advanceMeasureForAdd(c)) { c.selectedNoteIdx = 0; return insertTap(c, string, fret); }   // full → next measure
  return false;
}

// fretboard tap. Returns true if anything changed.
export function addNote(c: EditCtx, string: number, fret: number): boolean {
  const r = raw(c);
  if (c.insertArmed) {
    return insertTap(c, string, fret);
  }
  const sel = c.selectedNoteIdx;
  if (sel >= 0 && r[sel] && r[sel].rest) {
    const rest = r[sel];
    r[sel] = { string, fret, dur: rest.dur, fx: null };
    if (rest.chordLabel) r[sel].chordLabel = rest.chordLabel;
    return true;
  }
  if (sel >= 0 && r[sel] && !r[sel].rest) {
    const items = userBeats(r)[rawToBeatIndex(r, sel)].items;
    const same = items.find((it) => it.n.string === string);
    if (same) {
      if (same.n.fret === fret) {
        const wasBase = !same.n.chord;
        r.splice(same.idx, 1);
        if (wasBase && r[same.idx] && r[same.idx].chord) r[same.idx].chord = false;
        if (items.length > 1) {
          const remaining = items.filter((it) => it.idx !== same.idx)
            .map((it) => (it.idx > same.idx ? it.idx - 1 : it.idx));
          c.selectedNoteIdx = Math.min(...remaining);
        } else c.selectedNoteIdx = -1;
      } else {
        same.n.fret = fret; same.n.fx = null;
        c.selectedNoteIdx = same.idx;
      }
    } else {
      const lastIdx = items[items.length - 1].idx;
      r.splice(lastIdx + 1, 0, { string, fret, dur: items[0].n.dur, fx: null, chord: true });
      c.selectedNoteIdx = lastIdx + 1;
    }
    return true;
  }
  return addBeatNote(c, string, fret);
}

// fretboard ✕ tap — add a note then mark it dead/muted, regardless of select/add mode
export function addDead(c: EditCtx, string: number): void {
  if (!addNote(c, string, 0)) return;
  const r = raw(c);
  const i = c.selectedNoteIdx;
  if (i >= 0 && r[i] && !r[i].rest) r[i].fx = ["x"];
}

export function addRest(c: EditCtx, dur?: NoteDuration): void {
  const d = dur || c.curDur;
  const mk = (): NoteEvent => ({ rest: true, dur: d, ...(c.curRestDotted ? { dotted: true } : {}) });
  const r = raw(c);
  const i = c.selectedNoteIdx;
  if (!c.insertArmed && i >= 0 && r[i]) r[i] = mk();
  else if (c.insertArmed && i >= 0 && r[i]) { r.splice(i + 1, 0, mk()); c.selectedNoteIdx = i + 1; }
  else { r.push(mk()); c.selectedNoteIdx = -1; }
}

export function applyDur(c: EditCtx, d: NoteDuration): void {
  c.curDur = d;   // note-pen length
  if (c.insertArmed) return;   // 추가 mode: only arm the next note, don't relength the current one
  const r = raw(c);
  // change the selected note's length (only a real note — rests are handled by the rest pen)
  if (c.selectedNoteIdx >= 0 && r[c.selectedNoteIdx] && !r[c.selectedNoteIdx].rest) r[c.selectedNoteIdx].dur = d;
}

// delete the whole beat that note i belongs to; select the beat just before it
export function deleteNote(c: EditCtx, i: number): void {
  const r = raw(c);
  const bi = rawToBeatIndex(r, i);
  const groups = userBeats(r);
  const idxs = (bi >= 0 && groups[bi]) ? groups[bi].items.map((it) => it.idx) : [i];
  idxs.sort((a, b) => b - a).forEach((idx) => r.splice(idx, 1));
  const g = userBeats(r);
  if (!g.length) c.selectedNoteIdx = -1;
  else { const tb = Math.min(Math.max(0, (bi < 0 ? 0 : bi) - 1), g.length - 1); c.selectedNoteIdx = g[tb].items[0].idx; }
}

// ---- 범위(range) copy / cut / paste ----
export const cloneNote = (n: NoteEvent): NoteEvent => ({ ...n, fx: Array.isArray(n.fx) ? [...n.fx] : n.fx });

// raw note indices for the beats in [a..b] (inclusive, beat indices) of a note list
export function beatRangeRawIdx(notes: NoteEvent[], a: number, b: number): number[] {
  const groups = userBeats(notes);
  const lo = Math.min(a, b), hi = Math.max(a, b);
  const idxs: number[] = [];
  for (let bi = lo; bi <= hi; bi++) if (groups[bi]) groups[bi].items.forEach((it) => idxs.push(it.idx));
  return idxs;
}

// cut the beats in [a..b] from the active measure; return their (cloned) notes for the clipboard
export function cutRange(c: EditCtx, a: number, b: number): NoteEvent[] {
  const r = raw(c);
  const idxs = beatRangeRawIdx(r, a, b);
  const clip = idxs.map((i) => cloneNote(r[i]));
  idxs.slice().sort((x, y) => y - x).forEach((i) => r.splice(i, 1));
  const g = userBeats(r);
  const lo = Math.min(a, b);
  if (!g.length) c.selectedNoteIdx = -1;
  else { const tb = Math.min(Math.max(0, lo - 1), g.length - 1); c.selectedNoteIdx = g[tb].items[0].idx; }
  return clip;
}

// ---- cross-measure range (global {m,b} endpoints) ----
type Pos = { m: number; b: number };
const orderPos = (a: Pos, b: Pos): [Pos, Pos] =>
  (a.m < b.m || (a.m === b.m && a.b <= b.b)) ? [a, b] : [b, a];
const measureNotesOrRests = (c: EditCtx, m: number): NoteEvent[] =>
  (c.notes[m] && c.notes[m].length) ? c.notes[m] : restsForMeasure(c, m, (tsOf(c, m)[1] as NoteDuration) || 4);

// collect cloned notes across the range (read-only — synthesizes rests for un-materialized measures)
export function copyRangeGlobal(c: EditCtx, a: Pos, b: Pos): NoteEvent[] {
  const [lo, hi] = orderPos(a, b);
  const out: NoteEvent[] = [];
  for (let m = lo.m; m <= hi.m; m++) {
    const notes = measureNotesOrRests(c, m);
    const groups = userBeats(notes);
    const from = m === lo.m ? lo.b : 0;
    const to = m === hi.m ? hi.b : groups.length - 1;
    for (let bi = from; bi <= to; bi++) if (groups[bi]) groups[bi].items.forEach((it) => out.push(cloneNote(notes[it.idx])));
  }
  return out;
}

// quarter-beat duration a beat group occupies (grace = 0, dotted ×1.5, triplet ×⅔)
const groupDur = (n: NoteEvent): number => n.grace ? 0 : (4 / n.dur) * (n.dotted ? 1.5 : 1) * (n.triplet ? 2 / 3 : 1);
// build rests summing to qb quarter-beats — quarter rests first, then smaller (8th/16th/32nd) for any
// sub-quarter remainder (never half/whole, so cut regions read as a clean beat grid).
function restsForDuration(qb: number): NoteEvent[] {
  const units: [number, NoteDuration][] = [[1, 4], [0.5, 8], [0.25, 16], [0.125, 32]];
  const out: NoteEvent[] = [];
  let rem = qb;
  for (const [u, d] of units) while (rem >= u - 1e-6) { out.push({ rest: true, dur: d }); rem -= u; }
  return out;
}

// CUT: copy the range, then fill the cut region with quarter rests (smaller rests for any sub-quarter
// remainder) so every measure keeps its full length. Returns the clipboard.
export function cutRangeGlobal(c: EditCtx, a: Pos, b: Pos): NoteEvent[] {
  const [lo, hi] = orderPos(a, b);
  const clip = copyRangeGlobal(c, a, b);
  for (let m = lo.m; m <= hi.m; m++) {
    if (!c.notes[m] || !c.notes[m].length) c.notes[m] = restsForMeasure(c, m, (tsOf(c, m)[1] as NoteDuration) || 4);
    const groups = userBeats(c.notes[m]);
    const from = m === lo.m ? lo.b : 0;
    const to = m === hi.m ? hi.b : groups.length - 1;
    let cutDur = 0;
    for (let bi = from; bi <= to; bi++) if (groups[bi]) cutDur += groupDur(groups[bi].items[0].n);
    const fill = restsForDuration(cutDur);
    const out: NoteEvent[] = [];
    groups.forEach((g, bi) => {
      if (bi === from) out.push(...fill);          // quarter-rest fill replaces the whole cut span
      if (bi >= from && bi <= to) return;
      g.items.forEach((it) => out.push(it.n));
    });
    if (from >= groups.length) out.push(...fill);  // range past the end (defensive)
    c.notes[m] = out;
  }
  c.activeMeasure = lo.m;
  const g = userBeats(c.notes[lo.m] || []);
  c.selectedNoteIdx = g.length ? g[Math.min(Math.max(0, lo.b), g.length - 1)].items[0].idx : -1;
  return clip;
}

// paste clipboard notes right after the selected beat (shifting following beats along)
export function pasteAt(c: EditCtx, clip: NoteEvent[]): void {
  if (!clip || !clip.length) return;
  const r = raw(c);
  let at: number;
  if (c.selectedNoteIdx >= 0 && r[c.selectedNoteIdx]) {
    const bi = rawToBeatIndex(r, c.selectedNoteIdx);
    const g = userBeats(r);
    at = (bi >= 0 && g[bi]) ? g[bi].items[g[bi].items.length - 1].idx + 1 : c.selectedNoteIdx + 1;
  } else at = r.length;
  const clones = clip.map(cloneNote);
  r.splice(at, 0, ...clones);
  c.selectedNoteIdx = at + clones.length - 1;   // select the last pasted note
}

// OVERWRITE paste: from the selected beat, replace the next (clip-duration) worth of the timeline with
// the clip, FLOWING across measures and re-splitting so every affected bar stays exactly full.
type Beat = { notes: NoteEvent[]; dur: number };
const toBeats = (arr: NoteEvent[]): Beat[] => userBeats(arr).map((g) => ({ notes: g.items.map((it) => it.n), dur: groupDur(g.items[0].n) }));
const cloneBeat = (b: Beat): Beat => ({ notes: b.notes.map(cloneNote), dur: b.dur });

export function pasteOverwrite(c: EditCtx, clip: NoteEvent[]): void {
  if (!clip || !clip.length || c.activeMeasure < 0) return;
  const m0 = c.activeMeasure;
  if (!c.notes[m0] || !c.notes[m0].length) c.notes[m0] = restsForMeasure(c, m0, (tsOf(c, m0)[1] as NoteDuration) || 4);
  const capOf = (m: number) => { const ts = tsOf(c, m); return (ts[0] * 4) / ts[1]; };

  const m0beats = toBeats(c.notes[m0]);
  let startBi = c.selectedNoteIdx >= 0 ? rawToBeatIndex(c.notes[m0], c.selectedNoteIdx) : m0beats.length;
  if (startBi < 0) startBi = m0beats.length;

  const head = m0beats.slice(0, startBi);                 // kept before the selection
  const clipBeats = toBeats(clip).map(cloneBeat);
  const clipDur = clipBeats.reduce((s, b) => s + b.dur, 0);

  // walk from the selection, consuming the overwrite span (clipDur), flowing into following measures
  // ONLY as far as the clip reaches. Measures past that stay untouched (no float drift pulling them in).
  let remaining = clipDur, m = m0, pool = m0beats.slice(startBi);
  const affected = [m0];
  for (;;) {
    while (pool.length && remaining > 1e-6) { remaining -= pool[0].dur; pool.shift(); }
    if (remaining <= 1e-6) break;       // overwrite ended within measure m; `pool` is the kept post-region
    m++;
    if (!c.notes[m] || !c.notes[m].length) c.notes[m] = restsForMeasure(c, m, (tsOf(c, m)[1] as NoteDuration) || 4);
    affected.push(m);
    pool = toBeats(c.notes[m]);
  }
  const mLast = m;
  const padOvershoot = remaining < -1e-6 ? toBeats(restsForDuration(-remaining)) : [];   // last beat overshot
  const all: Beat[] = [...head, ...clipBeats, ...padOvershoot, ...pool];

  // re-split across the affected measures (m0..mLast), filling each to capacity (pad the last partial)
  const result: Record<number, NoteEvent[]> = {};
  let wm = m0, cur: NoteEvent[] = [], curDur = 0, selIdx = -1, selMeasure = m0, placed = 0;
  const selBeatNo = head.length;
  for (const beat of all) {
    // close the current bar when this beat overflows it, OR when a 0-duration grace beat would land on
    // an already-full bar (it should lead the next bar's note, not cling to the end of this one)
    const full = curDur >= capOf(wm) - 1e-6;
    if (cur.length && wm < mLast && ((curDur + beat.dur > capOf(wm) + 1e-6) || (beat.dur < 1e-6 && full))) {
      const pad = capOf(wm) - curDur;
      if (pad > 1e-6) cur.push(...restsForDuration(pad));
      result[wm] = cur; cur = []; curDur = 0; wm++;
    }
    if (placed === selBeatNo) { selMeasure = wm; selIdx = cur.length; }
    beat.notes.forEach((n) => cur.push(n));
    curDur += beat.dur; placed++;
  }
  if (cur.length || curDur > 0) { const pad = capOf(wm) - curDur; if (pad > 1e-6) cur.push(...restsForDuration(pad)); result[wm] = cur; }
  for (let mi = m0; mi <= mLast; mi++) if (!(mi in result)) result[mi] = restsForMeasure(c, mi, (tsOf(c, mi)[1] as NoteDuration) || 4);
  for (const k in result) c.notes[+k] = result[+k];

  c.activeMeasure = selMeasure;
  c.selectedNoteIdx = selIdx >= 0 ? selIdx : 0;
}

// set/clear the chord label on the selected beat (carried by the beat group's first item, which is
// what the alphaTex builder reads). Works on a rest too — a rest beat can label a chord change.
export function setChordLabel(c: EditCtx, label: string): void {
  const r = raw(c);
  const i = c.selectedNoteIdx;
  if (i < 0 || !r[i]) return;
  const groups = userBeats(r);
  const bi = rawToBeatIndex(r, i);
  const firstIdx = (bi >= 0 && groups[bi]) ? groups[bi].items[0].idx : i;
  const text = label.trim();
  if (text) r[firstIdx].chordLabel = text;
  else delete r[firstIdx].chordLabel;
}

// toggle a technique (stacking across groups, replacing within a group — e.g. bend + vibrato coexist).
// 추가 mode: arm the pen (next note). 선택 mode: toggle on the selected note.
export function setFx(c: EditCtx, fx: TechniqueId): void {
  if (c.insertArmed) { c.curFx = toggleFx(c.curFx, fx); return; }
  const r = raw(c);
  let i = c.selectedNoteIdx;
  if (i < 0 || (r[i] && r[i].rest)) { for (i = r.length - 1; i >= 0 && r[i] && r[i].rest; i--) { /* find last real */ } }
  if (i < 0 || !r[i] || r[i].rest) return;
  const next = toggleFx(fxList(r[i].fx), fx);
  r[i].fx = next.length ? next : null;
}

// toggle the tie (이음줄). 추가 mode: arm the pen. 선택 mode: toggle on the selected note.
export function setTie(c: EditCtx): void {
  if (c.insertArmed) { c.curTie = !c.curTie; return; }
  const r = raw(c);
  let i = c.selectedNoteIdx;
  if (i < 0 || (r[i] && r[i].rest)) { for (i = r.length - 1; i >= 0 && r[i] && r[i].rest; i--) { /* find last real */ } }
  if (i < 0 || !r[i] || r[i].rest) return;
  r[i].tie = !r[i].tie;
}

// toggle the augmentation dot (점음표). Applies to the selected beat (its primary note, so
// double-stops stay consistent) AND arms the pen so newly added notes inherit it.
export function toggleDot(c: EditCtx): void {
  if (c.insertArmed) { c.curDotted = !c.curDotted; return; }   // 추가 mode: arm next note only
  const r = raw(c);
  let i = c.selectedNoteIdx;
  if (i < 0 || (r[i] && r[i].rest)) { for (i = r.length - 1; i >= 0 && r[i] && r[i].rest; i--) { /* find last real */ } }
  if (i >= 0 && r[i] && !r[i].rest) {
    const bi = rawToBeatIndex(r, i);
    const firstIdx = (bi >= 0 && userBeats(r)[bi]) ? userBeats(r)[bi].items[0].idx : i;
    const next = !r[firstIdx].dotted;
    r[firstIdx].dotted = next;
    c.curDotted = next;
  } else {
    c.curDotted = !c.curDotted;   // nothing selected → just arm/disarm the pen
  }
}

// toggle the 3연음(triplet) flag on the selected beat's primary note AND arm the pen.
export function toggleTriplet(c: EditCtx): void {
  if (c.insertArmed) { c.curTriplet = !c.curTriplet; return; }   // 추가 mode: arm next note only
  const r = raw(c);
  let i = c.selectedNoteIdx;
  if (i < 0 || (r[i] && r[i].rest)) { for (i = r.length - 1; i >= 0 && r[i] && r[i].rest; i--) { /* find last real */ } }
  if (i >= 0 && r[i] && !r[i].rest) {
    const bi = rawToBeatIndex(r, i);
    const firstIdx = (bi >= 0 && userBeats(r)[bi]) ? userBeats(r)[bi].items[0].idx : i;
    const next = !r[firstIdx].triplet;
    r[firstIdx].triplet = next;
    c.curTriplet = next;
  } else {
    c.curTriplet = !c.curTriplet;
  }
}

// toggle a grace (꾸밈음) ornament with a hammer or slide transition. 추가 mode: arm the pen.
export function setGrace(c: EditCtx, slide: boolean): void {
  if (c.insertArmed) {
    const want = slide ? "sl" : "h";
    c.curGrace = c.curGrace === want ? "" : want;
    return;
  }
  const r = raw(c);
  let i = c.selectedNoteIdx;
  if (i < 0 || (r[i] && r[i].rest)) { for (i = r.length - 1; i >= 0 && r[i] && r[i].rest; i--) { /* find last real */ } }
  if (i < 0 || !r[i] || r[i].rest) return;
  const same = !!r[i].grace && !!r[i].graceSlide === slide;
  if (same) { r[i].grace = false; delete r[i].graceSlide; }   // re-applying the same kind clears it
  else { r[i].grace = true; if (slide) r[i].graceSlide = true; else delete r[i].graceSlide; }
}

// toggle the augmentation dot on the selected rest (점쉼표) AND arm the rest pen.
export function toggleRestDot(c: EditCtx): void {
  const r = raw(c);
  const i = c.selectedNoteIdx;
  if (i >= 0 && r[i] && r[i].rest) {
    const next = !r[i].dotted;
    r[i].dotted = next;
    c.curRestDotted = next;
  } else {
    c.curRestDotted = !c.curRestDotted;
  }
}
